import { Decimal } from 'decimal.js';
import { DEFAULT_CURRENCY, MONEY_SCALE } from '../constants/index.js';
/**
 * Decimal-safe money handling.
 *
 * Rule: money NEVER touches JavaScript `number` arithmetic. Values arrive as
 * strings (from Prisma `Decimal` columns or validated request bodies), are
 * computed with decimal.js, and are handed back as fixed-scale strings.
 *
 * `Money` is intentionally immutable — every operation returns a new instance.
 */
// Configure decimal.js once: banker's-free half-up rounding, generous precision.
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });
export class MoneyError extends Error {
    constructor(message) {
        super(message);
        this.name = 'MoneyError';
    }
}
function toDecimal(value) {
    if (value instanceof Money)
        return value.toDecimal();
    if (value instanceof Decimal)
        return value;
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            throw new MoneyError(`Cannot build Money from non-finite number: ${value}`);
        }
        // Numbers are accepted only as a convenience at the edges (e.g. quantities
        // in tests). They are routed through their string form so that a value such
        // as 0.1 + 0.2 never leaks binary float error into a stored amount.
        return new Decimal(value.toString());
    }
    const trimmed = value.trim();
    if (trimmed === '') {
        throw new MoneyError('Cannot build Money from an empty string');
    }
    let parsed;
    try {
        parsed = new Decimal(trimmed);
    }
    catch {
        throw new MoneyError(`Cannot build Money from string: "${value}"`);
    }
    if (!parsed.isFinite()) {
        throw new MoneyError(`Cannot build Money from non-finite string: "${value}"`);
    }
    return parsed;
}
export class Money {
    amount;
    currency;
    constructor(amount, currency) {
        this.amount = amount;
        this.currency = currency;
    }
    static of(value, currency = DEFAULT_CURRENCY) {
        return new Money(toDecimal(value), currency);
    }
    static zero(currency = DEFAULT_CURRENCY) {
        return new Money(new Decimal(0), currency);
    }
    /** Build from an integer number of sen (minor units). */
    static fromMinorUnits(minor, currency = DEFAULT_CURRENCY) {
        const asDecimal = toDecimal(minor);
        if (!asDecimal.isInteger()) {
            throw new MoneyError(`Minor units must be an integer, received: ${minor}`);
        }
        return new Money(asDecimal.dividedBy(new Decimal(10).pow(MONEY_SCALE)), currency);
    }
    /** Sum a list; returns zero in `currency` when the list is empty. */
    static sum(values, currency = DEFAULT_CURRENCY) {
        return values.reduce((acc, value) => acc.plus(value), Money.zero(currency));
    }
    assertSameCurrency(other) {
        if (other instanceof Money && other.currency !== this.currency) {
            throw new MoneyError(`Currency mismatch: cannot combine ${this.currency} with ${other.currency}`);
        }
    }
    plus(value) {
        this.assertSameCurrency(value);
        return new Money(this.amount.plus(toDecimal(value)), this.currency);
    }
    minus(value) {
        this.assertSameCurrency(value);
        return new Money(this.amount.minus(toDecimal(value)), this.currency);
    }
    /** Multiply by a unit-less factor (e.g. a quantity or a tax rate). */
    times(factor) {
        return new Money(this.amount.times(toDecimal(factor)), this.currency);
    }
    /** Divide by a unit-less divisor. Throws on divide-by-zero. */
    dividedBy(divisor) {
        const d = toDecimal(divisor);
        if (d.isZero())
            throw new MoneyError('Division by zero');
        return new Money(this.amount.dividedBy(d), this.currency);
    }
    /** `percent` is expressed in whole percent, e.g. 10 for 10%. */
    percentage(percent) {
        return new Money(this.amount.times(toDecimal(percent)).dividedBy(100), this.currency);
    }
    /** Round to the currency scale (2 dp for MYR) using half-up. */
    round() {
        return new Money(this.amount.toDecimalPlaces(MONEY_SCALE, Decimal.ROUND_HALF_UP), this.currency);
    }
    /** Clamp to zero so a discount can never push a total negative. */
    clampToZero() {
        return this.isNegative() ? Money.zero(this.currency) : this;
    }
    /** Return whichever of the two amounts is smaller. */
    min(other) {
        this.assertSameCurrency(other);
        const o = toDecimal(other);
        return this.amount.lessThanOrEqualTo(o) ? this : new Money(o, this.currency);
    }
    max(other) {
        this.assertSameCurrency(other);
        const o = toDecimal(other);
        return this.amount.greaterThanOrEqualTo(o) ? this : new Money(o, this.currency);
    }
    isZero() {
        return this.amount.isZero();
    }
    isNegative() {
        return this.amount.isNegative() && !this.amount.isZero();
    }
    isPositive() {
        return this.amount.greaterThan(0);
    }
    equals(other) {
        return this.amount.equals(toDecimal(other));
    }
    greaterThan(other) {
        return this.amount.greaterThan(toDecimal(other));
    }
    greaterThanOrEqual(other) {
        return this.amount.greaterThanOrEqualTo(toDecimal(other));
    }
    lessThan(other) {
        return this.amount.lessThan(toDecimal(other));
    }
    lessThanOrEqual(other) {
        return this.amount.lessThanOrEqualTo(toDecimal(other));
    }
    toDecimal() {
        return this.amount;
    }
    /**
     * Canonical persistence/transport form: a fixed-scale string such as
     * "129.90". This is what is written to Prisma `Decimal` columns and what is
     * sent over the API.
     */
    toString() {
        return this.amount.toFixed(MONEY_SCALE, Decimal.ROUND_HALF_UP);
    }
    toJSON() {
        return this.toString();
    }
    /** Integer sen, for gateways that quote amounts in minor units. */
    toMinorUnits() {
        return this.round().toDecimal().times(new Decimal(10).pow(MONEY_SCALE)).toNumber();
    }
    /**
     * Lossy: only for display/aggregation in charts, never for stored amounts
     * or arithmetic that feeds a stored amount.
     */
    toNumber() {
        return this.amount.toNumber();
    }
}
/** Convenience factory. */
export function money(value, currency = DEFAULT_CURRENCY) {
    return Money.of(value, currency);
}
/**
 * Format an amount for display, e.g. formatMoney("1299.5") -> "RM 1,299.50".
 * Display only — never feed the result back into arithmetic.
 */
export function formatMoney(value, currency = DEFAULT_CURRENCY, locale = 'en-MY') {
    const amount = Money.of(value, currency).round();
    const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: MONEY_SCALE,
        maximumFractionDigits: MONEY_SCALE,
    }).format(amount.toNumber());
    const symbol = currency === 'MYR' ? 'RM' : currency;
    return `${symbol} ${formatted}`;
}
/** True when a string is a well-formed money amount with <= 2 decimals. */
export function isValidMoneyString(value) {
    if (!/^-?\d+(\.\d{1,2})?$/.test(value.trim()))
        return false;
    try {
        Money.of(value);
        return true;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=money.js.map